import { Link } from 'react-router-dom';
import { Users, Calendar, MessageCircle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LandingPage() {
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_f697ed33-4c13-453f-bfe2-564f171bf1d2/artifacts/4slporqq_1%20%281%29.png";
  const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_239d7274-ca2b-497c-8252-9d7915b2e0dd/artifacts/cgzm8zjb_Group%20of%20Senior%20Woman%20Enjoying%20Being%20Together%20at%20a%20Cafe.jpg";

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mom's Biz Network" className="h-16 w-16" />
            <span className="text-xl font-bold text-[#5b7f95]">Mom's Biz Network</span>
          </div>
          <div className="flex gap-3">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full" data-testid="login-nav-btn">
                Log In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full px-8 shadow-sm" data-testid="join-nav-btn">
                Join Now
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-[#2d3748] mb-6">
                Women Supporting Women
              </h1>
              <p className="text-lg text-[#64748b] mb-8 leading-relaxed">
                Join South Africa's premier women's business networking community. Connect with like-minded entrepreneurs, attend inspiring events, and grow your business together.
              </p>
              <Link to="/register">
                <Button className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full px-12 py-6 text-lg shadow-md hover:shadow-lg" data-testid="hero-join-btn">
                  Become a Member
                </Button>
              </Link>
            </div>
            <div>
              <img 
                src={HERO_IMAGE} 
                alt="Women networking" 
                className="rounded-2xl shadow-lg w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#2d3748] mb-16">
            What We Offer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 bg-[#5b7f95]/10 rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[#5b7f95]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#2d3748]">Community</h3>
              <p className="text-[#64748b] leading-relaxed">
                Connect with 300+ women business owners across 9 regions in South Africa.
              </p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 bg-[#cb9944]/10 rounded-full flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-[#cb9944]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#2d3748]">Events</h3>
              <p className="text-[#64748b] leading-relaxed">
                Attend monthly meetings, workshops, and networking sessions designed for growth.
              </p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 bg-[#5b7f95]/10 rounded-full flex items-center justify-center mb-6">
                <MessageCircle className="w-8 h-8 text-[#5b7f95]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#2d3748]">Direct Messaging</h3>
              <p className="text-[#64748b] leading-relaxed">
                Connect one-on-one with other members for collaborations and support.
              </p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 bg-[#cb9944]/10 rounded-full flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-[#cb9944]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#2d3748]">Member Directory</h3>
              <p className="text-[#64748b] leading-relaxed">
                Discover members' businesses, services, and areas of expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#2d3748] mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-lg text-[#64748b] mb-8">
            Apply for membership today and start building meaningful connections.
          </p>
          <Link to="/register">
            <Button className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full px-12 py-6 text-lg shadow-md" data-testid="cta-join-btn">
              Apply for Membership
            </Button>
          </Link>
          <p className="mt-6 text-[#64748b]">
            Browse and buy from our members on the MBN website!
          </p>
          <a 
            href="https://www.momsbiznetwork.co.za" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 text-[#5b7f95] hover:text-[#4a6b80] font-semibold transition-colors"
          >
            www.momsbiznetwork.co.za
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2d3748] text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <img src={LOGO_URL} alt="Mom's Biz Network" className="h-24 w-24 mx-auto mb-4" />
          <p className="text-lg mb-2 font-bold">Mom's Biz Network</p>
          <p className="text-sm text-gray-400">Women Supporting Women</p>
          <p className="text-sm text-gray-400 mt-4">&copy; 2026 Mom's Biz Network. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
